import axios from 'axios';

// Base URL for API - use environment variable or default to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// External API URL for cohort details and fulfillment
const EXTERNAL_API_URL = process.env.REACT_APP_EXTERNAL_API_URL || 'http://localhost:8080';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 second timeout
});

/**
 * Capture PayPal payment on the server
 * @param {string} orderID - PayPal order ID
 * @returns {Promise<object>} Capture response with order details
 */
export const capturePayment = async (orderID) => {
  try {
    const response = await api.post('/api/capture', {
      orderID
    });

    if (response.data.success) {
      return {
        success: true,
        orderId: response.data.orderId,
        captureId: response.data.captureId,
        payerEmail: response.data.payerEmail,
        amount: response.data.amount,
        order: response.data.order
      };
    } else {
      throw new Error(response.data.message || 'Payment capture failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Fulfill PayPal payment and register user via external API
 * This endpoint handles payment capture internally
 * @param {string} orderID - PayPal order ID
 * @param {string} name - User's full name (optional, can be provided later)
 * @param {string} email - User's email address (optional, can be from PayPal)
 * @returns {Promise<object>} Registration response with affiliation code
 */
export const fulfillPayPalPayment = async (orderID, name = null, email = null) => {
  try {
    // External API endpoint
    const externalApiUrl = process.env.REACT_APP_EXTERNAL_API_URL || 'http://localhost:8080';

    const response = await axios.post(`${externalApiUrl}/api/v1/external/paypal/fulfill`, {
      orderId: orderID,
      name: name || null,
      email: email || null,
      amount: 199,
      timestamp: new Date().toISOString(),
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    // Handle response - adjust based on actual API response structure
    if (response.data.success || response.data.registration_code || response.data.registrationCode || response.data.affiliationCode) {
      return {
        success: true,
        // registrationCode: response.data.registration_code || response.data.registrationCode || response.data.affiliationCode,
        affiliationCode: response.data.affiliationCode || response.data.registration_code || response.data.registrationCode,
        message: response.data.message || 'Payment fulfilled successfully',
      };
    } else {
      throw new Error(response.data.message || 'Payment fulfillment failed');
    }
  } catch (error) {
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      throw new Error(
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      // Request made but no response received
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Register a user after payment completion (legacy - kept for backward compatibility)
 * @param {string} name - User's full name
 * @param {string} email - User's email address
 * @param {object} orderData - Order information from PayPal
 * @returns {Promise<object>} Registration response with code
 */
export const registerUser = async (name, email, orderData = {}) => {
  // Use the external API for fulfillment
  return fulfillPayPalPayment(orderData.orderId, name, email);
};

/**
 * Create Stripe Checkout Session
 * @param {object} checkoutData - Checkout session data
 * @returns {Promise<object>} Session response with sessionId
 */
/**
 * Fetch cohort details from API
 * @param {string} partnerId - Partner ID
 * @param {string} cohortId - Cohort ID
 * @returns {Promise<object>} Cohort details
 */
export const fetchCohortDetails = async (partnerId, cohortId) => {
  try {
    const response = await axios.get(`${EXTERNAL_API_URL}/api/v1/cohorts/partners/${partnerId}/cohorts/${cohortId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

export const createStripeCheckoutSession = async (checkoutData) => {
  try {
    const response = await axios.post(`${EXTERNAL_API_URL}/api/v1/external/stripe/create-checkout`, {
      productType: checkoutData.productType,
      amount: checkoutData.amount,
      currency: checkoutData.currency || 'usd',
      amountInSmallestUnit: checkoutData.amountInSmallestUnit, // Stripe unit_amount: cents (USD) or paise (INR)
      productName: checkoutData.productName,
      productDescription: checkoutData.productDescription,
      successUrl: checkoutData.successUrl,
      cancelUrl: checkoutData.cancelUrl,
      cohortId: checkoutData.cohortId,
      partnerId: checkoutData.partnerId,
      programId: checkoutData.programId,
      timezone: checkoutData.timezone,
      includePartner: checkoutData.includePartner,
      partnerDiscountPercent: checkoutData.partnerDiscountPercent,
      partnerAmount: checkoutData.partnerAmount,
      baseAmount: checkoutData.baseAmount,
      timestamp: new Date().toISOString(),
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    if (response.data.sessionId || response.data.url) {
      return {
        success: true,
        sessionId: response.data.sessionId,
        url: response.data.url, // Checkout session URL for direct redirect
      };
    } else {
      throw new Error(response.data.message || 'Failed to create checkout session');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

/**
 * Fulfill Stripe payment and register user via external API
 * @param {string} sessionId - Stripe checkout session ID
 * @returns {Promise<object>} Registration response with affiliation code
 */
export const fulfillStripePayment = async (sessionId) => {
  try {
    const response = await axios.post(`${EXTERNAL_API_URL}/api/v1/external/stripe/fulfill`, {
      sessionId: sessionId,
      timestamp: new Date().toISOString(),
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    if (response.data.success || response.data.affiliationCode) {
      return {
        success: true,
        affiliationCode: response.data.affiliationCode || response.data.registration_code || response.data.registrationCode,
        message: response.data.message || 'Payment fulfilled successfully',
      };
    } else {
      throw new Error(response.data.message || 'Payment fulfillment failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message ||
        `Server error: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error('Network error: Unable to reach server. Please check your connection.');
    } else {
      throw new Error(error.message || 'An unexpected error occurred');
    }
  }
};

export default api;

